/**
 * Validation utilities for the rebooking process
 */

/**
 * Interface for segment mapping result
 */
export interface SegmentMapping {
  // ID of the original segment
  originalSegmentId: string;
  // Index of the new leg in the journey
  newLegIndex?: number;
  // Match type (exact, partial, fallback)
  matchType: 'exact' | 'partial' | 'fallback';
  // Flag to indicate all new legs replace this segment (for fallback)
  allNewLegs?: boolean;
}

/**
 * Function to check if booking has provisional changes
 */
export const hasProvisionalChanges = (bookingData: any): boolean => {
  if (!bookingData) return false

  return (
    bookingData.last_after_sales_operation === 'AFTERSALES_REBOOK' ||
    bookingData.outbound_booking_tariff_segments?.some((segment: any) =>
      segment.required_products?.some((product: any) => product.provisional)
    ) ||
    bookingData.inbound_booking_tariff_segments?.some((segment: any) =>
      segment.required_products?.some((product: any) => product.provisional)
    )
  )
}

/**
 * Extract station code from segment or leg
 * @param item Segment or leg object
 * @param type 'departure' or 'arrival'
 */
const getStationCode = (item: any, type: 'departure' | 'arrival'): string => {
  const stationKey = `${type}_station`;
  return (
    item?.[stationKey]?._u_i_c_station_code ||
    item?.[stationKey]?.short_code ||
    ''
  );
};

/**
 * Get service name from segment or leg
 */
const getServiceName = (item: any): string => {
  // Try different ways to get service name
  if (item.service_identifier) {
    const parts = item.service_identifier.split('|');
    if (parts.length > 1) return parts[1]; // Extract service name from identifier
  }
  return item.service_name || '';
};

/**
 * Find the origin station of a journey
 */
const findJourneyOrigin = (segments: any[]): string => {
  if (!segments || !segments.length) return '';
  
  // For journey segments, first segment's departure is the origin
  return getStationCode(segments[0], 'departure');
};

/**
 * Find the final destination of a journey
 */
const findJourneyDestination = (segments: any[]): string => {
  if (!segments || !segments.length) return '';
  
  // For journey segments, last segment's arrival is the destination
  return getStationCode(segments[segments.length - 1], 'arrival');
};

/**
 * Check if there's a partial match between journeys
 * This helps identify cases where part of the journey changes but some segments remain the same
 */
const isPartialJourneyMatch = (originalSegments: any[], newLegs: any[]): boolean => {
  // Look for any matching segments between original and new journeys
  for (const originalSegment of originalSegments) {
    const originalDeparture = getStationCode(originalSegment, 'departure');
    const originalArrival = getStationCode(originalSegment, 'arrival');
    
    for (const newLeg of newLegs) {
      const newDeparture = getStationCode(newLeg, 'departure');
      const newArrival = getStationCode(newLeg, 'arrival');
      
      // If we find any segment that matches exactly, there's a partial match
      if (originalDeparture === newDeparture && originalArrival === newArrival) {
        return true;
      }
    }
  }
  
  return false;
};

/**
 * Find exact segment matches between original and new journey
 */
const findExactSegmentMatches = (originalSegments: any[], newLegs: any[]): SegmentMapping[] => {
  const mappings: SegmentMapping[] = [];
  
  for (const newLeg of newLegs) {
    const legDeparture = getStationCode(newLeg, 'departure');
    const legArrival = getStationCode(newLeg, 'arrival');
    
    // Find matching original segment with exact station codes
    const matchingSegment = originalSegments.find(segment => 
      getStationCode(segment, 'departure') === legDeparture &&
      getStationCode(segment, 'arrival') === legArrival
    );
    
    if (matchingSegment) {
      mappings.push({
        originalSegmentId: matchingSegment.uuid || matchingSegment.id,
        newLegIndex: newLegs.indexOf(newLeg),
        matchType: 'exact'
      });
    }
  }
  
  return mappings;
};

/**
 * Map segments when structure changes (split, combine, etc.)
 */
const mapSegmentsWithStructureChanges = (originalSegments: any[], newLegs: any[]): SegmentMapping[] => {
  const mappings: SegmentMapping[] = [];
  
  // First check for exact matches
  const exactMatches = findExactSegmentMatches(originalSegments, newLegs);
  mappings.push(...exactMatches);
  
  // If we have some exact matches, but not for all segments, handle partial matching
  if (exactMatches.length > 0 && 
      (exactMatches.length < originalSegments.length || exactMatches.length < newLegs.length)) {
    
    // Find which original segments aren't matched yet
    const matchedOriginalIds = new Set(exactMatches.map(m => m.originalSegmentId));
    const unmatchedOriginals = originalSegments.filter(
      segment => !matchedOriginalIds.has(segment.uuid || segment.id)
    );
    
    // Find which new legs aren't matched yet
    const matchedLegIndices = new Set(exactMatches.map(m => m.newLegIndex));
    const unmatchedLegs = newLegs.filter((_, index) => !matchedLegIndices.has(index));
    
    // For remaining unmatched segments/legs, create partial mappings
    if (unmatchedOriginals.length > 0 && unmatchedLegs.length > 0) {
      // Map each unmatched original segment to all unmatched new legs
      unmatchedOriginals.forEach(segment => {
        mappings.push({
          originalSegmentId: segment.uuid || segment.id,
          matchType: 'partial',
          allNewLegs: true
        });
      });
    }
  } 
  // If no exact matches at all, fall back to complete mapping
  else if (exactMatches.length === 0) {
    return mapCompleteRouteChange(originalSegments, newLegs);
  }
  
  return mappings;
};

/**
 * Map segments for completely different routes
 */
const mapCompleteRouteChange = (originalSegments: any[], newLegs: any[]): SegmentMapping[] => {
  // For complete route changes, we map all original segments to all new legs
  // This effectively implements the "fallback" scenario from documentation
  
  return originalSegments.map(segment => ({
    originalSegmentId: segment.uuid || segment.id,
    allNewLegs: true,
    matchType: 'fallback'
  }));
};

/**
 * Match journeys at a holistic level rather than strict segment-by-segment matching
 * Supports all rebooking scenarios: one-to-one, split, combine, origin replacements, merge, and fallback
 * 
 * @param originalJourneySegments Original booking journey segments
 * @param newJourneyLegs New journey legs to match against
 * @param direction OUTBOUND or INBOUND
 * @returns Array of matched segment mappings with replacement instructions
 */
export const matchJourneys = (
  originalJourneySegments: any[], 
  newJourneyLegs: any[],
  direction: string
): SegmentMapping[] => {
  if (!originalJourneySegments?.length || !newJourneyLegs?.length) {
    console.log(`[DEBUG] Empty journey segments provided for ${direction}`);
    return [];
  }

  // Extract journey endpoints (origin and final destination)
  const originalJourneyOrigin = findJourneyOrigin(originalJourneySegments);
  const originalJourneyDestination = findJourneyDestination(originalJourneySegments);
  const newJourneyOrigin = findJourneyOrigin(newJourneyLegs);
  const newJourneyDestination = findJourneyDestination(newJourneyLegs);

  console.log(`[DEBUG] Original ${direction} journey: ${originalJourneyOrigin} → ${originalJourneyDestination} (${originalJourneySegments.length} segments)`);
  console.log(`[DEBUG] New ${direction} journey: ${newJourneyOrigin} → ${newJourneyDestination} (${newJourneyLegs.length} segments)`);

  // Case 1: One-to-one exact matches (precise station and service matches)
  const exactMatches = findExactSegmentMatches(originalJourneySegments, newJourneyLegs);
  if (exactMatches.length > 0 && exactMatches.length === originalJourneySegments.length && 
      exactMatches.length === newJourneyLegs.length) {
    console.log(`[DEBUG] Found exact one-to-one segment matches for all segments`);
    return exactMatches;
  }

  // Case 2: Check for route structure changes (split, combine, origin replacements, merge)
  // If journey endpoints match, but segment structure differs
  if ((originalJourneyOrigin === newJourneyOrigin && originalJourneyDestination === newJourneyDestination) ||
      isPartialJourneyMatch(originalJourneySegments, newJourneyLegs)) {
    console.log(`[DEBUG] Journey endpoints match or partial match found but segment structure differs`);
    return mapSegmentsWithStructureChanges(originalJourneySegments, newJourneyLegs);
  }

  // Case 3: Complete route change (different origin/destination)
  if (originalJourneyOrigin !== newJourneyOrigin || originalJourneyDestination !== newJourneyDestination) {
    console.log(`[DEBUG] Complete route change detected`);
    return mapCompleteRouteChange(originalJourneySegments, newJourneyLegs);
  }

  // Fallback: No valid mapping found
  console.warn(`[WARN] No valid segment mapping found between original and new journeys`);
  return [];
};
